import { useEffect, useRef, type CSSProperties, type DetailedHTMLProps, type HTMLAttributes } from 'react'
import { cn } from '../../lib/cn.js'

/**
 * Eco, the frog from Peter's chat-button kit, in React.
 *
 * The element is the kit's own `<eco-avatar>` (`eco-avatar.js`, loaded by
 * `main.tsx` so that jsdom never sees it): a 3D head drawn in SVG whose eyes
 * follow the pointer and whose every clip starts and ends on the same resting
 * face. `mood` is what Eco is doing for as long as it lasts — thinking while
 * a reply is on its way, talking while it arrives. `flash` is one clip on top
 * (happy when something went through, alert when a card waits, error when it
 * failed), after which the mood carries on. Reduced motion draws a still
 * face. Decision 116.
 */

export type EcoMood = 'idle' | 'listening' | 'thinking' | 'talking' | 'sleeping'
export type EcoFlash = 'happy' | 'alert' | 'error' | 'listening' | null

type AvatarAttributes = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
  follow?: ''
  motion?: 'on' | 'off'
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'eco-avatar': AvatarAttributes
    }
  }
}

interface AvatarElement extends HTMLElement {
  play?(state: string, options?: { loop?: boolean }): unknown
}

const LOOPS: ReadonlySet<string> = new Set(['thinking', 'talking', 'listening', 'sleeping'])

export function EcoAvatar({
  mood = 'idle',
  flash = null,
  onFlashed,
  follow = true,
  className,
  style,
}: {
  mood?: EcoMood
  flash?: EcoFlash
  /** Called when the one-off clip has played, so the caller can clear it. */
  onFlashed?: () => void
  follow?: boolean
  className?: string
  style?: CSSProperties
}) {
  const ref = useRef<AvatarElement>(null)
  const flashed = useRef(onFlashed)
  flashed.current = onFlashed

  useEffect(() => {
    const element = ref.current
    if (!element?.play || flash) return
    element.play(mood, { loop: LOOPS.has(mood) })
  }, [mood, flash])

  useEffect(() => {
    const element = ref.current
    if (!flash) return
    if (!element?.play) {
      flashed.current?.()
      return
    }
    const done = () => flashed.current?.()
    element.addEventListener('eco-animation-end', done, { once: true })
    element.play(flash)
    return () => element.removeEventListener('eco-animation-end', done)
  }, [flash])

  return (
    <eco-avatar
      ref={ref}
      aria-hidden="true"
      follow={follow ? '' : undefined}
      className={cn('block shrink-0', className)}
      style={style}
    />
  )
}

/**
 * The kit's small Eco icon: the frog as squares, for buttons where the live
 * head would be too small to read. In the brand's greens.
 */
export function EcoMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={cn('size-6 shrink-0', className)}>
      <rect x="2" y="12" width="28" height="18" rx="8" fill="var(--color-brand-600)" />
      <rect x="3" y="4" width="12" height="12" rx="6" fill="var(--color-brand-600)" />
      <rect x="17" y="4" width="12" height="12" rx="6" fill="var(--color-brand-600)" />
      <rect x="7" y="23" width="18" height="6" rx="3" fill="var(--color-brand-200)" />
      <rect x="5" y="6" width="8" height="8" rx="4" fill="#fff" />
      <rect x="19" y="6" width="8" height="8" rx="4" fill="#fff" />
      <rect x="7" y="8" width="4" height="4" rx="1.5" fill="var(--color-ink-900)" />
      <rect x="21" y="8" width="4" height="4" rx="1.5" fill="var(--color-ink-900)" />
      <rect x="7" y="8" width="1.5" height="1.5" rx="0.5" fill="#fff" />
      <rect x="21" y="8" width="1.5" height="1.5" rx="0.5" fill="#fff" />
      <rect x="11" y="22" width="10" height="2" rx="1" fill="var(--color-ink-900)" />
      <rect x="9" y="20" width="2" height="2" rx="1" fill="var(--color-ink-900)" />
      <rect x="21" y="20" width="2" height="2" rx="1" fill="var(--color-ink-900)" />
    </svg>
  )
}
